<template lang="pug">
.generic-wrapper
  .container
    BackButton(name="StatisticsOverview")
    h1.title Vermietungsübersicht
    .sub-headline Hier finden Sie eine detaillierte Übersicht Ihrer Einnahmen. Nutzen Sie die Filteroptionen, um die Daten nach Ihren Bedürfnissen anzupassen.
    .spacer.sm

    b-collapse(:open="false")
      template(#trigger="props")
        b-button(type="is-primary" size="is-small") {{ props.open ? 'Filter schließen' : 'Filter öffnen' }}
      .calendar-config.margin-top
        p <b>Schritt 1:</b> Datum auswählen
        .spacer.sm
        .flex
          b-field(group)
            v-date-picker(locale="de" :columns="1" v-model="dateRange.start" :input-debounce="100")
              template(v-slot="{ inputValue, inputEvents }")
                b-field(label="Startdatum" label-position="on-border")
                  input.input(:value="inputValue" v-on="inputEvents")
          .spacer.sm.inline
          v-date-picker(locale="de" :columns="1" v-model="dateRange.end" :input-debounce="100")
            template(v-slot="{ inputValue, inputEvents }")
              b-field(label="Enddatum" label-position="on-border")
                input.input(:value="inputValue" v-on="inputEvents")

      .calendar-config.margin-top
        p <b>Schritt 2:</b> Eigentümer auswählen
        .spacer.sm
        b-field(group)
          b-dropdown(
            v-model="ownerFilter"
            scrollable
            :max-height="200"
            multiple
            aria-role="list")
            template(#trigger)
              b-button(type="is-flat" icon-right="menu-down") {{ ownerFilter.length }} von {{ owners.length }} ausgewählt
            b-dropdown-item(v-for="owner in owners" :value="owner.id") {{ owner.fullName }}

      .calendar-config.margin-top
        p <b>Schritt 3:</b> Auswahl der Ferienobjekte
        .spacer.sm
        b-field(group)
          b-dropdown(
            v-model="rentObjectIdsFilter"
            scrollable
            :max-height="200"
            multiple
            aria-role="list")
            template(#trigger)
              b-button(type="is-flat" icon-right="menu-down") {{ rentObjectIdsFilter.length }} von {{ rentObjectIds.length }} ausgewählt
            b-dropdown-item(v-for="rentObject in rentObjectIds" :value="rentObject.id") {{ rentObject.code }}: {{ rentObject.name }}

      .calendar-config.margin-top
        p <b>Schritt 4:</b> Buchungselemente auswählen
        .spacer.sm
        b-field(group)
          b-dropdown(
            v-model="uniqueNamesFilter"
            scrollable
            :max-height="200"
            multiple
            aria-role="list")
            template(#trigger)
              b-button(type="is-flat" icon-right="menu-down") {{ uniqueNamesFilter.length }} von {{ uniqueNames.length }} ausgewählt
            b-dropdown-item(v-for="item in uniqueNames" :value="item") {{ item }}

      template(v-if="ownerFilter.length > 0")
        .spacer.md
        b-button(type="is-primary" icon-left="file-excel" @click="print()") Drucken

  .container
    .flex.center
      .card.margin-top
        .top
          .left
            .card-headline Zahlungen erhalten
            .card-description Alle Fewo-Zahlungen die in diesem Zeitraum {{ formatDate(dateRange.start.toISOString()) }} bis {{ formatDate(dateRange.end.toISOString()) }} eingegangen sind.
          .right
          .big-number {{ toCurrency((bookingItemsProcessed.totalIn - bookingItemsProcessed.totalInLeft) / 100) }}
      .card.margin-top
        .top
          RentObjectGroupStackedBarChart(:data="[bookingItemsChartsLast, bookingItemsCharts, bookingItemsChartsNext]" :uniqueGroups="uniqueRentObjectGroups")

  .container
    .card.margin-top
      .top
        .left
          .flex-card
            .one
              .card-headline Ausgewählter Zeitraum
              .card-description {{ formatDate(dateRange.start.toISOString()) }} bis {{ formatDate(dateRange.end.toISOString()) }}
            .right(v-if="differenceLastYearvsCurrent <= 0")
              .loader-outer
                .loader-inner(:style="{ width: (Math.abs(differenceLastYearvsCurrent) / bookingItemsProcessedLast.totalIn) * 100 + '%' }")
              .card-description Noch {{ formatShort(Math.abs(differenceLastYearvsCurrent)) }} bis zum Vorjahr
            .right(v-else)
              .loader-outer
                .loader-inner(:style="{ width: (Math.abs(differenceLastYearvsCurrent) / bookingItemsProcessedLast.totalIn) * 100 + '%' }")
              .card-description Besser als im Vorjahr um {{ formatShort(differenceLastYearvsCurrent) }}
        .grid
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Vermietung
              .right
              .big-number {{ toCurrency((bookingItemsProcessed.totalIn - bookingItemsProcessed.totalInLeft) / 100) }}
                span +{{ toCurrency((bookingItemsProcessed.totalInLeft) / 100) }}
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Extras
              .right
              .big-number {{ toCurrency(bookingItemsProcessed.totalBookingItems / 100) }}
    .card.margin-top
      .top
        .left
          .card-headline Zeitraum letztes Jahr
          .card-description {{ formatDate(addYears(dateRange.start.toISOString(), -1)) }} bis {{ formatDate(addYears(dateRange.end.toISOString(), -1)) }}
        .right
        .grid
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Vermietung
              .right
              .big-number {{ toCurrency((bookingItemsProcessedLast.totalIn - bookingItemsProcessedLast.totalInLeft) / 100) }}
                span +{{ toCurrency((bookingItemsProcessedLast.totalInLeft) / 100) }}
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Extras
              .right
              .big-number {{ toCurrency(bookingItemsProcessedLast.totalBookingItems / 100) }} 
    .card.margin-top
      .top
        .left
          .card-headline Zeitraum letztes Jahr (Vergleich gleicher Tag bis heute)
          .card-description {{ formatDate(addYears(dateRange.start.toISOString(), -1)) }} bis {{ formatDate(addYears(new Date().toISOString(), -1)) }}
        .right
        .grid
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Vermietung
              .right
              .big-number {{ toCurrency((bookingItemsProcessedLastUpToSameDay.totalIn - bookingItemsProcessedLastUpToSameDay.totalInLeft) / 100) }}
                span +{{ toCurrency((bookingItemsProcessedLastUpToSameDay.totalInLeft) / 100) }}
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Extras
              .right
              .big-number {{ toCurrency(bookingItemsProcessedLastUpToSameDay.totalBookingItems / 100) }}
    .card.margin-top
      .top
        .left
          .card-headline Zeitraum nächstes Jahr
          .card-description {{ formatDate(addYears(dateRange.start.toISOString(), 1)) }} bis {{ formatDate(addYears(dateRange.end.toISOString(), 1)) }}
        .grid
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Vermietung
              .right
              .big-number {{ toCurrency((bookingItemsProcessedNext.totalIn - bookingItemsProcessedNext.totalInLeft) / 100) }}
                span +{{ toCurrency((bookingItemsProcessedNext.totalInLeft) / 100) }}
          .card.margin-top.border
            .top
              .left
                .card-headline FeWo-Extras
              .right
              .big-number {{ toCurrency(bookingItemsProcessedNext.totalBookingItems / 100) }}
    </template>

<script>
import { DateTime } from 'luxon';
import RentObjectGroupStackedBarChart from "@/components/organisms/RentObjectGroupStackedBarChart.vue";

export default {
  components: {
    RentObjectGroupStackedBarChart
  },
  data() {
    return {
      isConfigVisible: false,
      rootYear: new Date().getFullYear() - 10,
      currentYear: new Date().getFullYear(),
      year: new Date().getFullYear(),
      currentMonth: new Date().getMonth(),
      month: -1,
      ownerFilter: [7, 8, 9, 10],
      uniqueNames: [],
      uniqueNamesFilter: [],
      uniqueRentObjectGroups: [],
      rentObjectIds: [],
      rentObjectIdsFilter: [],
      summary: {
        arrivalCount: 0,
        bookingItems: [],
        payments: [],
      },
      summaryLast: {
        arrivalCount: 0,
        bookingItems: [],
        payments: [],
      },
      summaryNext: {
        arrivalCount: 0,
        bookingItems: [],
        payments: [],
      },
      summaryLastUpToSameDay: {
        arrivalCount: 0,
        bookingItems: [],
        payments: [],
      },
      bookingItemsProcessed: [],
      bookingItemsProcessedLast: [],
      bookingItemsProcessedNext: [],
      bookingItemsCharts: [],
      bookingItemsChartsLast: [],
      bookingItemsChartsNext: [],
      bookingItemsProcessedLastUpToSameDay: [],
      dateRange: {
        start: new Date(new Date().getFullYear(), 0, 1, 0, 1),
        end: new Date(new Date().getFullYear(), 11, 31, 23, 59)
      },
      dateOptions: {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
      }
    }
  },
  watch: {
    dateRange: {
      async handler() {
        const loadingComponent = this.$buefy.loading.open();
        this.getNewData();
        loadingComponent.close();
      },
      deep: true
    },
    async ownerFilter(newVal) {
      this.updateOwnerSelection(newVal);
    },
    uniqueNames(newVal) {
      this.uniqueNamesFilter = newVal;
    },
    uniqueNamesFilter() {
      this.bookingItemsProcessed = this.processItems(this.summary);
    },
    rentObjectIdsFilter() {
      this.getNewData();
    }
  },
  computed: {
    owners() {
      return this.$store.state.owners.all;
    },
    differenceLastYearvsCurrent() {
      return this.bookingItemsProcessed.totalIn - this.bookingItemsProcessedLast.totalIn;
    },
  },
  methods: {
    addYears(date, years) {
      return DateTime.fromISO(date).plus({ years }).toISODate();
    },
    async getNewData() {
      if (this.ownerFilter.length <= 0) return;
      const loadingComponent = this.$buefy.loading.open();

      // Fetch existing summaries
      this.summary = await this.$store.dispatch("rentObjectBookings/findBookingItems", { ...this.dateRange, filter: this.ownerFilter });

      const dateRangeLastYear = {
        start: new Date(this.dateRange.start.getFullYear() - 1, this.dateRange.start.getMonth(), this.dateRange.start.getDate(), 0, 1),
        end: new Date(this.dateRange.end.getFullYear() - 1, this.dateRange.end.getMonth(), this.dateRange.end.getDate(), 23, 59)
      };
      this.summaryLast = await this.$store.dispatch("rentObjectBookings/findBookingItems", { ...dateRangeLastYear, filter: this.ownerFilter, last: true });

      const dateRangeNextYear = {
        start: new Date(this.dateRange.start.getFullYear() + 1, this.dateRange.start.getMonth(), this.dateRange.start.getDate(), 0, 1),
        end: new Date(this.dateRange.end.getFullYear() + 1, this.dateRange.end.getMonth(), this.dateRange.end.getDate(), 23, 59)
      };
      this.summaryNext = await this.$store.dispatch("rentObjectBookings/findBookingItems", { ...dateRangeNextYear, filter: this.ownerFilter, next: true });

      // Fetch data for last year up to the same day
      const today = new Date();
      const todayLastYear = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate(), 23, 59);
      const dateRangeLastUpToSameDay = {
        start: new Date(this.dateRange.start.getFullYear() - 1, this.dateRange.start.getMonth(), this.dateRange.start.getDate(), 0, 1),
        end: todayLastYear
      };
      this.summaryLastUpToSameDay = await this.$store.dispatch("rentObjectBookings/findBookingItemsCreatedAt", {
        ...dateRangeLastUpToSameDay,
        filter: this.ownerFilter
      });

      // Process all summaries
      this.getUniqueNames();
      this.bookingItemsProcessed = this.processItems(this.summary);
      this.bookingItemsProcessedLast = this.processItems(this.summaryLast);
      this.bookingItemsProcessedNext = this.processItems(this.summaryNext);
      this.bookingItemsProcessedLastUpToSameDay = this.processItems(this.summaryLastUpToSameDay);

      this.uniqueRentObjectGroups = this.getUniqueRentObjectGroups();

      this.bookingItemsCharts = this.processBookingItemsCharts(this.dateRange.start.getFullYear(), this.bookingItemsProcessed.table);
      this.bookingItemsChartsLast = this.processBookingItemsCharts(this.dateRange.start.getFullYear() - 1, this.bookingItemsProcessedLast.table);
      this.bookingItemsChartsNext = this.processBookingItemsCharts(this.dateRange.start.getFullYear() + 1, this.bookingItemsProcessedNext.table);

      loadingComponent.close();
    },
    getUniqueNames() {
      this.uniqueNames = this.summary.bookingItems.reduce((total, item) => {
        if (total.toString().includes(item.name) || item.includeCommission === true) {
          return total;
        }
        return total + ',' + item.name
      }, 0)
      if (this.uniqueNames && this.uniqueNames.length > 0) {
        this.uniqueNames = this.uniqueNames.split(',');
        this.uniqueNames.shift();
      } else {
        this.uniqueNames = [];
      }
    },
    processBookingItemsCharts(label, table) {
      const dataFromAllGroups = [];
      for (const group of this.uniqueRentObjectGroups) {
        const filteredData = table.filter(item => item.parentName === group);
        dataFromAllGroups.push({
          name: group,
          totalIn: filteredData.reduce((total, item) => {
            return total + item.totalIn
          }, 0),
          totalInLeft: filteredData.reduce((total, item) => {
            return total + item.totalInLeft
          }, 0),
          totalOut: filteredData.reduce((total, item) => {
            return total + item.totalOut
          }, 0),
          totalCommission: filteredData.reduce((total, item) => {
            return total + item.totalCommission
          }, 0),
        });
      }

      return {
        label,
        stacks: dataFromAllGroups.map((item) => {
          return {
            label: item.name,
            value: item.totalIn
          }
        })
      }
    },
    processItems({ arrivals, bookingItems, payments }) {
      const table = [];

      let uniqueNameCopy = this.uniqueNames;
      if (this.uniqueNamesFilter.length > 0) {
        uniqueNameCopy = this.uniqueNames.filter(u => this.uniqueNamesFilter.find(un => un === u));
      }

      for (let i = 0; i < arrivals.length; i += 1) {
        const rentObjectBookingId = arrivals[i].id;
        const rentObjectName = arrivals[i].rentObject.name;
        const filteredBookingItems = bookingItems.filter(b => b.rentObjectBookingId === rentObjectBookingId);
        const filteredPayments = payments.filter(b => b.rentObjectBookingId === rentObjectBookingId);

        let totalPayments = 0;
        if (filteredPayments.length > 0) {
          totalPayments = filteredPayments.reduce((total, item) => {
            return total + item.total
          }, 0)
        }

        let totalIn = 0;
        if (filteredBookingItems.length > 0) {
          totalIn = filteredBookingItems.reduce((total, item) => {
            if (item.includeCommission) {
              return total + item.totalPrice
            }
            return total;
          }, 0)
        }

        let totalInLeft = totalIn - totalPayments < 0 ? 0 : totalIn - totalPayments;

        let totalOut = 0;
        if (filteredBookingItems.length > 0) {
          totalOut = filteredBookingItems.reduce((total, item) => {
            if (item.includeCommission) {
              return total + item.totalSettlement
            }
            return total;
          }, 0)
        }

        let totalCommission = 0;
        if (filteredBookingItems.length > 0) {
          totalCommission = filteredBookingItems.reduce((total, item) => {
            if (item.includeCommission) {
              return total + item.totalPrice - item.totalSettlement
            }
            return total;
          }, 0)
        }

        let totalBookingItems = 0;
        const uniqueCount = {};
        for (let u = 0; u < uniqueNameCopy.length; u += 1) {
          const name = uniqueNameCopy[u];
          uniqueCount['item' + u] = filteredBookingItems.reduce((total, item) => {
            if (name === item.name) {
              totalBookingItems += item.totalPrice;
              return total + item.totalPrice
            }
            return total;
          }, 0);
        }

        table.push({
          name: rentObjectName,
          parentName: arrivals[i].rentObject.parent.name,
          ownerName: arrivals[i].rentObject.owner.fullName,
          totalIn,
          totalInLeft,
          totalOut,
          totalCommission,
          totalBookingItems,
          ...uniqueCount
        })
      }

      const totalInLeftSum = table.reduce((total, item) => {
        return total + item.totalInLeft
      }, 0);

      const totalInSum = table.reduce((total, item) => {
        return total + item.totalIn
      }, 0);

      const totalOutSum = table.reduce((total, item) => {
        return total + item.totalOut
      }, 0);

      const totalCommissionSum = table.reduce((total, item) => {
        return total + item.totalCommission
      }, 0);

      const totalBookingItemsSum = table.reduce((total, item) => {
        return total + item.totalBookingItems
      }, 0);

      const uniqueCount = {};
      for (let u = 0; u < uniqueNameCopy.length; u += 1) {
        uniqueCount['item' + u] = table.reduce((total, item) => {
          return total + item['item' + u];
        }, 0);
      }

      return {
        totalIn: totalInSum,
        totalInLeft: totalInLeftSum,
        totalOut: totalOutSum,
        totalCommission: totalCommissionSum,
        totalBookingItems: totalBookingItemsSum,
        ...uniqueCount,
        table
      }
    },
    formatShort(num) {
      num = Math.round(num / 100);
      if (num >= 1e9) {
        return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'b';
      }
      if (num >= 1e6) {
        return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'm';
      }
      if (num >= 1e3) {
        return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'k';
      }
      return num.toString();
    },
    formatDateUrl(d) {
      const dt = DateTime.fromJSDate(d);
      return dt.setLocale('de')
        .toFormat("dd-LL-yy")
    },
    formatDate(d) {
      const dt = DateTime.fromISO(d);
      return dt.setLocale('de')
        .toFormat("dd.LL.yy")
    },
    formatDateTime(d) {
      if (d instanceof Date) {
        const dt = DateTime.fromJSDate(d);
        return dt.setLocale('de')
          .toFormat("dd.LL.yyyy")
      }
      const dt = DateTime.fromISO(d);
      return dt.setLocale('de')
        .toFormat("dd.LL.yyyy")
    },
    toCurrency(value) {
      if (typeof value !== "number") {
        return value;
      }
      var formatter = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      });
      return formatter.format(value);
    },
    async print() {
      // todo
    },
    updateOwnerSelection(newVal) {
      const loadingComponent = this.$buefy.loading.open();
      this.rentObjectIds = this.owners.filter(o => newVal.includes(o.id)).map(o => o.rentObjects).reduce(
        function (accumulator, currentValue) {
          return accumulator.concat(currentValue);
        },
        []
      );
      this.rentObjectIdsFilter = this.rentObjectIds.map(n => n.id);
      loadingComponent.close();
    },
    getUniqueRentObjectGroups() {
      const current = this.bookingItemsProcessed.table.reduce((total, item) => {
        return total.toString().includes(item.parentName) ? total : total + ',' + item.parentName
      }, '') ?? ''
      const last = this.bookingItemsProcessedLast.table.reduce((total, item) => {
        return total.toString().includes(item.parentName) ? total : total + ',' + item.parentName
      }, '') ?? ''
      const next = this.bookingItemsProcessedNext.table.reduce((total, item) => {
        return total.toString().includes(item.parentName) ? total : total + ',' + item.parentName
      }, '') ?? ''

      const merged = current.split(',').concat((last).split(',')).concat((next).split(','));
      const unique = [...new Set(merged)];
      return unique.filter(n => n !== '0' && n !== '');
    }
  },
  async mounted() {
    await this.$store.dispatch("owners/findAll");
    this.updateOwnerSelection(this.ownerFilter);
  },
}
</script>

<style lang="scss" scoped>
.items-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  .item .item-price {
    font-size: 1.2rem;
    font-weight: 600;
  }
}

.th-wrap {
  justify-content: flex-end;
}

.big-number {
  font-size: 2rem;
  font-weight: 600;

  span {
    color: lightgray;
    margin-left: 24px;
  }
}

.flex {
  display: flex;
}

.flex.center {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.border {
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.right {
  display: flex;
  justify-content: flex-end;
  align-items: start;

  .loader-outer {
    width: 100px;
    height: 10px;
    position: relative;
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  .loader-inner {
    width: 50%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #00d1b2;
    border-radius: 3px;
  }
}

.flex-card {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .right {
    display: flex;
    align-items: end;
    flex-direction: column;
  }
}
</style>